import { appVersionApi } from '../api/appVersionApi';
import type { AppVersionItem, CreateAppVersionRequest, UpdateAppVersionRequest, AppVersionFilterParams } from '../types/appVersion';
import { useBaseQuery, useBaseMutation, useNoDataMutation, useInvalidateQueries } from './useBaseQuery';
import { PaginationData } from '../models/paginationData';

export const appVersionKeys = {
    all: ['appVersion'] as const,
    lists: () => [...appVersionKeys.all, 'list'] as const,
    list: (params: AppVersionFilterParams) => [...appVersionKeys.lists(), params] as const,
};

export const useAppVersions = (params: AppVersionFilterParams) => {
    return useBaseQuery<PaginationData<AppVersionItem>>(
        appVersionKeys.list(params),
        () => appVersionApi.getVersions(params),
        { staleTime: 10_000 },
    );
};

export const useCreateAppVersion = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<CreateAppVersionRequest, AppVersionItem>(
        (data) => appVersionApi.create(data),
        {
            onSuccess: () => invalidate(appVersionKeys.lists()),
        },
    );
};

export const useUpdateAppVersion = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<{ id: number; data: UpdateAppVersionRequest }, AppVersionItem>(
        ({ id, data }) => appVersionApi.update(id, data),
        {
            onSuccess: () => invalidate(appVersionKeys.lists()),
        },
    );
};

export const useDeleteAppVersion = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<number>(
        (id) => appVersionApi.remove(id),
        {
            onSuccess: () => invalidate(appVersionKeys.lists()),
        },
    );
};
